import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { PrimeReactProvider } from 'primereact/api';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Layout from './components/Layout';
import PublicLayout from './components/PublicLayout';
import BackgroundProcessIndicator from './components/BackgroundProcessIndicator';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import NewProjectPage from './pages/NewProjectPage';
import ProjectWorkflowPage from './pages/ProjectWorkflowPage';
import EditProjectPage from './pages/EditProjectPage';
import ProfilePage from './pages/ProfilePage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AwaitingApprovalPage from './components/AwaitingApprovalPage';
import HomePage from './pages/HomePage';
import PlatformPage from './pages/PlatformPage';
import WorkflowPage from './pages/WorkflowPage';
import SolutionsPage from './pages/SolutionsPage';
import ContactPage from './pages/ContactPage';
import ResourcesPage from './pages/ResourcesPage';

// PrimeReact theme and styles
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

// Protected Route Component (requires approved status)
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  console.log('ProtectedRoute - isAuthenticated:', isAuthenticated, 'user status:', user?.status, 'loading:', loading);

  if (loading) {
    return (
      <div className="flex align-items-center justify-content-center min-h-screen">
        <i className="pi pi-spin pi-spinner text-4xl"></i>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('ProtectedRoute - User not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // Check if user is pending or suspended
  if (user?.status === 'pending') {
    console.log('ProtectedRoute - User is pending, showing awaiting approval page');
    return <Navigate to="/app/awaiting-approval" replace />;
  }

  if (user?.status === 'suspended') {
    console.log('ProtectedRoute - User is suspended, redirecting to profile');
    return <Navigate to="/app/profile" replace />;
  }

  console.log('ProtectedRoute - User authenticated and approved, showing protected content');
  return <>{children}</>;
};

// Authenticated Route Component (any status - for profile and awaiting approval pages)
const AuthenticatedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  console.log('AuthenticatedRoute - isAuthenticated:', isAuthenticated, 'loading:', loading);

  if (loading) {
    return (
      <div className="flex align-items-center justify-content-center min-h-screen">
        <i className="pi pi-spin pi-spinner text-4xl"></i>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('AuthenticatedRoute - User not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  console.log('AuthenticatedRoute - User authenticated, showing content');
  return <>{children}</>;
};

// Admin Route Component (requires super admin role)
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  console.log('AdminRoute - isAuthenticated:', isAuthenticated, 'isSuperAdmin:', user?.isSuperAdmin, 'loading:', loading);

  if (loading) {
    return (
      <div className="flex align-items-center justify-content-center min-h-screen">
        <i className="pi pi-spin pi-spinner text-4xl"></i>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('AdminRoute - User not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  if (!user?.isSuperAdmin) {
    console.log('AdminRoute - User is not super admin, redirecting to dashboard');
    return <Navigate to="/app/dashboard" replace />;
  }

  console.log('AdminRoute - User is super admin, showing admin content');
  return <>{children}</>;
};

// Public Route Component (for marketing pages)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

// Auth Route Component (redirects to dashboard if authenticated)
const AuthRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  console.log('AuthRoute - isAuthenticated:', isAuthenticated, 'loading:', loading);

  if (loading) {
    return (
      <div className="flex align-items-center justify-content-center min-h-screen">
        <i className="pi pi-spin pi-spinner text-4xl"></i>
      </div>
    );
  }

  if (isAuthenticated) {
    console.log('AuthRoute - User is authenticated, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  console.log('AuthRoute - User not authenticated, showing login page');
  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Marketing Routes */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <PublicLayout />
          </PublicRoute>
        }
      >
        <Route index element={<HomePage />} />
        <Route path="platform" element={<PlatformPage />} />
        <Route path="platform/workflow" element={<WorkflowPage />} />
        <Route path="solutions" element={<SolutionsPage />} />
        <Route path="resources" element={<ResourcesPage />} />
        <Route path="contact" element={<ContactPage />} />
      </Route>

      {/* Authentication Route */}
      <Route
        path="/login"
        element={
          <AuthRoute>
            <LoginPage />
          </AuthRoute>
        }
      />

      {/* Protected Application Routes */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/new" element={<NewProjectPage />} />
        <Route path="projects/:projectId" element={<ProjectWorkflowPage />} />
        <Route path="projects/:projectId/edit" element={<EditProjectPage />} />
        <Route
          path="admin"
          element={
            <AdminRoute>
              <AdminDashboardPage />
            </AdminRoute>
          }
        />
      </Route>

      {/* Authenticated Routes (any status) */}
      <Route
        path="/app"
        element={
          <AuthenticatedRoute>
            <Layout />
          </AuthenticatedRoute>
        }
      >
        <Route path="profile" element={<ProfilePage />} />
        <Route path="awaiting-approval" element={<AwaitingApprovalPage />} />
      </Route>

      {/* Legacy redirects for existing authenticated users */}
      <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/projects" element={<Navigate to="/app/projects" replace />} />

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <PrimeReactProvider>
        <AuthProvider>
          <Router>
            <AppRoutes />
            <BackgroundProcessIndicator 
              position="top-right"
              maxVisible={3}
              showCompleted={true}
            />
          </Router>
        </AuthProvider>
      </PrimeReactProvider>
    </ErrorBoundary>
  );
};

export default App;